import { useState, useEffect, useCallback } from 'react';
import { getCourseReviewsApi, submitCourseReviewApi } from '../../../api/apiServices';
import { useAuth } from '../../../context/useAuth';

export default function CourseReviewsTab({ courseId, isEnrolled }) {
  const { isAuthenticated, user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({
    totalReviews: 0,
    averageRating: 4.8,
    distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
    percentageDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [submitError, setSubmitError] = useState('');
  
  // Review form state
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [hasReviewed, setHasReviewed] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const starLabels = {
    1: 'Needs Improvement',
    2: 'Fair',
    3: 'Good Course',
    4: 'Very Good & Insightful',
    5: 'Exceptional & Highly Recommended!'
  };

  const loadReviews = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getCourseReviewsApi(courseId);
      if (res.success) {
        setReviews(res.reviews || []);
        if (res.stats) setStats(res.stats);
        if (res.userReview) {
          setHasReviewed(true);
          setRating(res.userReview.rating || 5);
          setTitle(res.userReview.title || '');
          setComment(res.userReview.comment || '');
        }
      }
    } catch (err) {
      console.warn("Could not load course reviews:", err.message);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    if (courseId) {
      loadReviews();
    }
  }, [courseId, loadReviews]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess('');

    if (!rating || rating < 1) {
      setSubmitError('Please select a star rating.');
      return;
    }

    if (!comment.trim()) {
      setSubmitError('Please enter your feedback comments.');
      return;
    }

    try {
      setSubmitting(true);
      const res = await submitCourseReviewApi(courseId, {
        rating,
        title: title.trim(),
        comment: comment.trim()
      });

      if (res.success) {
        setSubmitSuccess(hasReviewed ? 'Your review has been updated!' : 'Thank you for your feedback! Your review is published.');
        setHasReviewed(true);
        setShowForm(false);
        // Refresh reviews
        await loadReviews();
      } else {
        setSubmitError(res.message || 'Failed to submit review.');
      }
    } catch (err) {
      setSubmitError(err.response?.data?.message || 'Error submitting review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Overview & Rating Breakdown */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 md:p-8 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          {/* Main Score */}
          <div className="md:col-span-4 text-center md:text-left border-b md:border-b-0 md:border-r border-slate-100 pb-6 md:pb-0 md:pr-8">
            <p className="text-xs font-semibold text-teal-600 uppercase tracking-wider mb-1">Student Rating</p>
            <div className="flex items-baseline justify-center md:justify-start gap-2">
              <span className="text-5xl font-extrabold text-slate-900 tracking-tight">
                {stats.averageRating ? stats.averageRating.toFixed(1) : '4.8'}
              </span>
              <span className="text-slate-400 text-lg font-medium">/ 5.0</span>
            </div>
            <div className="flex items-center justify-center md:justify-start gap-1 my-2">
              {[1, 2, 3, 4, 5].map((s) => {
                const filled = s <= Math.round(stats.averageRating || 5);
                return (
                  <i 
                    key={s} 
                    className={`ri-star-fill text-lg ${filled ? 'text-amber-400' : 'text-slate-200'}`} 
                  />
                );
              })}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Based on {stats.totalReviews} verified student review{stats.totalReviews === 1 ? '' : 's'}
            </p>
          </div>

          {/* Star Distribution Bars */}
          <div className="md:col-span-8 space-y-2">
            {[5, 4, 3, 2, 1].map((star) => {
              const count = stats.distribution?.[star] || 0;
              const percent = stats.percentageDistribution?.[star] || (stats.totalReviews > 0 ? Math.round((count / stats.totalReviews) * 100) : (star === 5 ? 85 : star === 4 ? 15 : 0));
              return (
                <div key={star} className="flex items-center gap-3 text-xs">
                  <span className="w-12 font-medium text-slate-600 flex items-center gap-1 justify-end">
                    {star} <i className="ri-star-fill text-amber-400 text-[11px]" />
                  </span>
                  <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden">
                    <div 
                      className="bg-amber-400 h-full rounded-full transition-all duration-500"
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                  <span className="w-10 text-right text-slate-400 font-medium">{percent}%</span>
                  <span className="w-8 text-right text-slate-400">({count})</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Action button to open review form */}
        <div className="mt-6 pt-6 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h4 className="text-sm font-semibold text-slate-800">
              {hasReviewed ? 'You have reviewed this course' : 'Have you studied this course?'}
            </h4>
            <p className="text-xs text-slate-500 mt-0.5">
              {hasReviewed ? 'You can update your rating and feedback at any time.' : 'Share your genuine experience to help fellow Nigerian students and the instructor.'}
            </p>
          </div>

          {isAuthenticated ? (
            <button
              onClick={() => setShowForm(!showForm)}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold rounded-xl transition-all shadow-sm cursor-pointer"
            >
              <i className={showForm ? 'ri-close-line' : hasReviewed ? 'ri-edit-line' : 'ri-chat-smile-3-line'} />
              {showForm ? 'Cancel Form' : hasReviewed ? 'Edit My Review' : 'Write a Review'}
            </button>
          ) : (
            <a
              href="/login"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-xl transition-all cursor-pointer"
            >
              <i className="ri-lock-line text-slate-500" />
              Sign In to Leave Feedback
            </a>
          )}
        </div>
      </div>

      {/* Review Submission Form Modal/Card */}
      {showForm && (
        <div className="bg-white rounded-2xl border-2 border-teal-500/30 p-6 md:p-8 shadow-md">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="font-bold text-base text-slate-900">
                {hasReviewed ? 'Update Your Course Review' : 'Rate & Review This Course'}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Posting as <strong className="text-teal-700">{user?.name || user?.email}</strong>
              </p>
            </div>
            <span className="text-xs px-2.5 py-1 rounded-full bg-teal-50 text-teal-700 font-medium">
              Verified Student
            </span>
          </div>

          {submitError && (
            <div className="p-3.5 mb-4 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700 flex items-center gap-2">
              <i className="ri-error-warning-line text-base text-rose-500" />
              {submitError}
            </div>
          )}

          {submitSuccess && (
            <div className="p-3.5 mb-4 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-700 flex items-center gap-2">
              <i className="ri-checkbox-circle-line text-base text-emerald-500" />
              {submitSuccess}
            </div>
          )}

          <form onSubmit={handleSubmitReview} className="space-y-4">
            {/* Star Selector */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Overall Rating <span className="text-rose-500">*</span>
              </label>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((s) => {
                    const active = (hoverRating || rating) >= s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setRating(s)}
                        onMouseEnter={() => setHoverRating(s)}
                        onMouseLeave={() => setHoverRating(0)}
                        className="p-1 cursor-pointer transition-transform hover:scale-110 focus:outline-none"
                      >
                        <i 
                          className={`ri-star-fill text-2xl transition-colors ${
                            active ? 'text-amber-400 drop-shadow-sm' : 'text-slate-200 hover:text-amber-200'
                          }`} 
                        />
                      </button>
                    );
                  })}
                </div>
                <span className="text-xs font-semibold text-slate-700 ml-2 px-2.5 py-1 bg-slate-100 rounded-lg">
                  {starLabels[hoverRating || rating]}
                </span>
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Headline / Short Summary (Optional)
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Concise, practical and directly relevant to Nigerian industry standards!"
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none"
                maxLength={120}
              />
            </div>

            {/* Comment */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Your Detailed Feedback <span className="text-rose-500">*</span>
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="What did you enjoy most about this course? How did the instructor's delivery and real-world projects help you? What could be improved?"
                rows={4}
                required
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-teal-500 focus:outline-none leading-relaxed"
                maxLength={1500}
              />
              <div className="flex justify-between text-[11px] text-slate-400 mt-1">
                <span>Please be constructive and respectful.</span>
                <span>{comment.length} / 1500</span>
              </div>
            </div>

            {/* Submit & Cancel */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-all shadow-md cursor-pointer"
              >
                {submitting ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill" />
                    {hasReviewed ? 'Update Review' : 'Publish Review'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Reviews List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-base text-slate-900 flex items-center gap-2">
            <span>Student Feedback</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
              {reviews.length}
            </span>
          </h3>
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl p-10 text-center border border-slate-100">
            <div className="w-8 h-8 border-3 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-xs text-slate-500">Loading student reviews...</p>
          </div>
        ) : reviews.length === 0 ? (
          <div className="bg-white rounded-2xl p-10 text-center border border-slate-100">
            <div className="w-14 h-14 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-3 text-amber-500 text-2xl">
              <i className="ri-star-smile-line" />
            </div>
            <h4 className="font-bold text-slate-800 text-sm mb-1">No reviews yet</h4>
            <p className="text-xs text-slate-500 max-w-sm mx-auto mb-4">
              Be the first Nigerian learner to share your experience with this curriculum!
            </p>
            {isAuthenticated && !showForm && (
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold rounded-xl transition-all"
              >
                <i className="ri-edit-line" /> Write the First Review
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {reviews.map((rev) => {
              const reviewDate = rev.createdAt 
                ? new Date(rev.createdAt).toLocaleDateString('en-NG', { month: 'short', day: 'numeric', year: 'numeric' })
                : 'Recent';

              return (
                <div 
                  key={rev._id} 
                  className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm hover:border-slate-200 transition-all space-y-4"
                >
                  {/* Top user header */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                      {rev.studentAvatar ? (
                        <img 
                          src={rev.studentAvatar} 
                          alt={rev.studentName} 
                          className="w-10 h-10 rounded-full object-cover border border-slate-200" 
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
                          {(rev.studentName || 'S').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-bold text-xs sm:text-sm text-slate-900">{rev.studentName}</h4>
                          {rev.verifiedPurchase && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md">
                              <i className="ri-checkbox-circle-fill text-emerald-600" /> Verified Learner
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-400">
                          <div className="flex items-center">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <i 
                                key={s} 
                                className={`ri-star-fill text-[13px] ${s <= rev.rating ? 'text-amber-400' : 'text-slate-200'}`} 
                              />
                            ))}
                          </div>
                          <span>•</span>
                          <span>{reviewDate}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Review Content */}
                  <div>
                    {rev.title && (
                      <h5 className="font-semibold text-sm text-slate-900 mb-1">{rev.title}</h5>
                    )}
                    <p className="text-xs sm:text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                      {rev.comment}
                    </p>
                  </div>

                  {/* Instructor Official Response (if present) */}
                  {rev.instructorReply && rev.instructorReply.comment && (
                    <div className="mt-3 bg-teal-50/60 border border-teal-100 rounded-xl p-4 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          {rev.instructorReply.instructorAvatar ? (
                            <img 
                              src={rev.instructorReply.instructorAvatar} 
                              alt="Instructor" 
                              className="w-6 h-6 rounded-full object-cover border border-teal-300"
                            />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-teal-600 text-white flex items-center justify-center text-[10px] font-bold">
                              <i className="ri-user-star-line" />
                            </div>
                          )}
                          <span className="text-xs font-bold text-teal-900">
                            {rev.instructorReply.instructorName || 'Course Instructor'}
                          </span>
                          <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 bg-teal-600 text-white rounded">
                            Instructor Response
                          </span>
                        </div>
                        <span className="text-[11px] text-teal-700/70">
                          {rev.instructorReply.repliedAt 
                            ? new Date(rev.instructorReply.repliedAt).toLocaleDateString('en-NG', { month: 'short', day: 'numeric', year: 'numeric' })
                            : 'Official Reply'}
                        </span>
                      </div>
                      <p className="text-xs text-slate-700 leading-relaxed pl-8">
                        {rev.instructorReply.comment}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
